/**
 * TrendsV2Skin — Transaction-page Trends v2 presentation.
 *
 * Renders the 7-row decision hierarchy bound by the UX contract
 * (`docs/superpowers/decisions/2026-07-19-trends-v2-ux-contract.md`) against
 * the resolved `TrendsV2ResolvedState` provided by `<TrendsV2Adapter />`.
 *
 *   1. Window banner         (trends-v2-window-banner)
 *   2. Comparison KPI strip  (trends-v2-kpi-{spending|income|net})
 *   3. Top drivers           (trends-v2-top-categories, trends-v2-top-merchants)
 *   4. Recurring vs one-off  (trends-v2-recurring-vs-oneoff)
 *   5. Anomaly callouts      (trends-v2-anomalies)
 *   6. Net-spend area chart  (trends-v2-chart)
 *   7. Granularity radio     (trends-v2-granularity-{day|week|month})
 *
 * Drill-through rows carry `trends-v2-drill-{kind}-{id}` selectors and compose
 * URLs via the canonical `filtersToParams` helper from
 * `useFilterUrlSync.ts:98` so the existing `/transactions` hydration
 * path round-trips unchanged.
 *
 * Loading / error / empty shells are owned by the adapter — this skin renders
 * only inside the "resolved" branch where `data.meta.transactionCount > 0`.
 */
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowDown,
  ArrowUp,
  ChevronRight,
  Info,
  Minus,
} from 'lucide-react';

import type { TrendsV2ResolvedState } from './TrendsV2Adapter';
import { cn } from '@/lib/utils';
import { IconButton } from '@/components/ui/icon-button';
import { filtersToParams } from '@/hooks/useFilterUrlSync';
import type { FilterState } from '@/lib/filters';
import { useDataToken } from '@/lib/design/widgetTokens';
import { useCurrency } from '@/hooks/useCurrency';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { INSIGHT_CHART_CONTAINER_CLASS, INSIGHT_CHART_MARGIN } from '@/lib/chartLayout';
import type {
  ResolvedTransactionTrendsGranularity,
  TransactionTrendsDelta,
} from '@/lib/resolverContracts';

// ── Public prop ─────────────────────────────────────────────────────────────

export interface TrendsV2SkinProps {
  state: TrendsV2ResolvedState;
}

// ── Local helpers ───────────────────────────────────────────────────────────

type DeltaSign = 'up' | 'down' | 'flat';

interface DeltaChannels {
  /** Semantic token name resolved via `useDataToken` at the call site. */
  tokenName: 'positive' | 'negative' | 'neutral';
  textClass: string;
  icon: typeof ArrowUp;
  sign: '+' | '−' | '';
  label: string;
  shortLabel: string;
  userFlavour: DeltaSign;
}

function classifyDelta(delta: TransactionTrendsDelta | undefined): DeltaSign {
  if (!delta || !Number.isFinite(delta.absolute)) return 'flat';
  if (delta.absolute > 0) return 'up';
  if (delta.absolute < 0) return 'down';
  return 'flat';
}

function deltaChannels(
  delta: TransactionTrendsDelta | undefined,
  direction: 'spending' | 'income' | 'net',
  fmt: (v: number) => string,
): DeltaChannels {
  const sign = classifyDelta(delta);
  const absolute = delta?.absolute ?? 0;
  const absPretty = fmt(Math.abs(absolute));
  // For spending, an increase is financially negative; for income/net,
  // an increase is positive. This flips the user-meaningful direction
  // while keeping the raw `sign` (numeric) intact for screen-reader
  // exactness.
  const userFlavour: DeltaSign =
    direction === 'spending'
      ? sign === 'up'
        ? 'down'
        : sign === 'down'
          ? 'up'
          : 'flat'
      : sign;
  const textClass =
    userFlavour === 'up'
      ? 'text-data-positive'
      : userFlavour === 'down'
        ? 'text-data-negative'
        : 'text-data-neutral';
  const icon =
    userFlavour === 'up' ? ArrowUp : userFlavour === 'down' ? ArrowDown : Minus;
  const signGlyph =
    sign === 'up' ? '+' : sign === 'down' ? '−' : '';
  const verb =
    sign === 'up' ? 'up' : sign === 'down' ? 'down' : 'flat';
  // `direction` is informational — user-facing copy is always
  // "vs prior period" per UX contract §9.2.
  void direction;
  return {
    tokenName:
    userFlavour === 'up' ? 'positive' : userFlavour === 'down' ? 'negative' : 'neutral',
    textClass,
    icon,
    sign: signGlyph as '+' | '−' | '',
    label: `${verb} ${absPretty} vs prior period`,
    shortLabel: `${signGlyph}${absPretty}`,
    userFlavour,
  };
}

function safePercent(delta: TransactionTrendsDelta | undefined): string {
  if (!delta || delta.percent == null || !Number.isFinite(delta.percent)) {
    return '';
  }
  const value = Math.abs(delta.percent);
  const formatted =
    value >= 100 ? value.toFixed(0) : value >= 10 ? value.toFixed(1) : value.toFixed(1);
  return `${formatted}%`;
}

function granularLongForm(granularity: ResolvedTransactionTrendsGranularity): string {
  return granularity; // "day" | "week" | "month" matches ARIA labels in the contract.
}

function bucketLabel(bucket: string, granularity: ResolvedTransactionTrendsGranularity): string {
  // Bucket keys are `YYYY-MM-DD` for day, `YYYY-Www` for ISO week, `YYYY-MM` for month.
  // Render a short, locale-agnostic form so the chart axis does not balloon at mobile widths.
  if (granularity === 'month') return bucket;
  if (granularity === 'week') {
    const isoWeekMatch = /^(\d{4})-W(\d{2})$/.exec(bucket);
    if (isoWeekMatch) return `W${isoWeekMatch[2]}`;
    return bucket;
  }
  // day: bucket is YYYY-MM-DD → render MM/DD
  const dayMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(bucket);
  if (dayMatch) return `${dayMatch[2]}/${dayMatch[3]}`;
  return bucket;
}

function formatPrettyDate(iso: string): string {
  // Render ISO date in a compact human form. The user's full dateFormat
  // preference lives in `useTimezone` — we keep this side-effect-free so
  // it is safe to call inside an aria-label builder.
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  return `${month} ${day}, ${year}`;
}

function merchantSlug(merchant: string): string {
  return merchant.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

// ── Drill-through URL composition ────────────────────────────────────────────

interface DrillDriver {
  kind: 'category' | 'merchant' | 'anomaly';
  /** A stable, lowercase, ASCII-safe identifier for the testid suffix. */
  id: string;
  /** Human label echoed in the row. */
  label: string;
}

function buildDrillFilter(
  base: FilterState,
  driver: DrillDriver,
  bucket?: string,
): FilterState {
  const extended: FilterState = { ...base };
  if (driver.kind === 'category') {
    extended.categoryIds = [driver.id];
    return extended;
  }
  if (driver.kind === 'merchant') {
    // Shared `q=merchant:"…"` syntax — preserve any prior category/account
    // narrowing and append the merchant term in the canonical slot.
    const nextSearch = `merchant:"${driver.label}"`;
    extended.search = extended.search ? `${extended.search} ${nextSearch}` : nextSearch;
    return extended;
  }
  // anomaly: only day-kind anomalies carry a bucket the user might
  // want to scroll to — but per UX contract §3 the anomaly card does NOT
  // mutate the URL for day-bucket anomalies (active window unchanged).
  // Category/merchant anomalies drill through with one driver field, same
  // shape as the top-driver rows.
  if (bucket && /^\d{4}-\d{2}-\d{2}$/.test(bucket)) {
    // keep active window; bucket is informational only
  }
  return extended;
}

function useDrillHref(baseFilter: FilterState) {
  return useCallback(
    (driver: DrillDriver, bucket?: string): string => {
      const extended = buildDrillFilter(baseFilter, driver, bucket);
      const qs = filtersToParams(extended).toString();
      return qs ? `/transactions?${qs}` : '/transactions';
    },
    [baseFilter],
  );
}

function navigateFromEvent(
  navigate: ReturnType<typeof useNavigate>,
  href: string,
  event: React.MouseEvent | React.KeyboardEvent,
) {
  // Allow modifier-clicks (cmd/ctrl/shift/meta) and middle-click to behave
  // like normal anchor interactions — only intercept left-click without
  // modifiers so the browser can open in a new tab via the URL.
  if ('button' in event && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
    event.preventDefault();
    navigate(href);
  }
}

// ── Sub-components ───────────────────────────────────────────────────────────

interface RowProps {
  label: string;
  total: string;
  delta?: TransactionTrendsDelta;
  href?: string;
  drillKind?: 'category' | 'merchant' | 'anomaly';
  drillId?: string;
  ariaName?: string;
  onNavigate?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  testId: string;
  drillTestId?: string;
}

function DriverRow({
  label,
  total,
  delta,
  href,
  drillKind,
  drillId,
  ariaName,
  onNavigate,
  testId,
  drillTestId,
}: RowProps) {
  const channels = useMemo(
    () =>
      deltaChannels(delta, 'spending', (v) => `$${v.toFixed(2)}`),
    [delta],
  );
  const Icon = channels.icon;
  const hasDrill = Boolean(href && onNavigate && drillKind && drillId);
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={hasDrill ? onNavigate : undefined}
      onKeyDown={
        hasDrill
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigate?.(e);
              }
            }
          : undefined
      }
      aria-label={ariaName ?? label}
      className={cn(
        'group flex w-full min-h-[44px] items-start gap-2 rounded-md border border-transparent px-2 py-2 text-left',
        'hover:border-border focus-visible:border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-data-neutral',
        hasDrill && 'cursor-pointer',
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1 break-words">
        <span data-role="driver-title" className="break-words text-sm font-medium text-foreground">{label}</span>
        <span data-role="driver-metrics" className="flex min-w-0 items-center gap-2 tabular-nums">
          <span data-role="driver-total" className="shrink-0 text-sm font-semibold text-foreground">{total}</span>
          {delta ? (
            <span
              className={cn('flex items-center gap-0.5 text-xs font-medium', channels.textClass)}
              data-sign={channels.sign === '+' ? 'positive' : channels.sign === '−' ? 'negative' : 'neutral'}
            >
              <Icon className="h-3 w-3" aria-hidden="true" />
              <span aria-hidden="true">{channels.sign}</span>
              <span className="sr-only">{channels.label}</span>
            </span>
          ) : null}
        </span>
      </span>
      {hasDrill && drillKind && drillId ? (
        <span
          role="img"
          aria-label="View transactions in this window"
          data-testid={drillTestId ?? `trends-v2-drill-${drillKind}-${drillId}`}
          className={cn(
            'inline-flex h-7 w-7 min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-md text-muted-foreground',
            'group-hover:text-foreground',
          )}
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </span>
      ) : null}
    </button>
  );
}

interface KpiCardProps {
  testId: string;
  kind: 'spending' | 'income' | 'net';
  label: string;
  current: number;
  previous: number;
  delta: TransactionTrendsDelta;
  fmt: (v: number) => string;
  onNavigate?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  href?: string;
}

function KpiCard({ testId, kind, label, current, previous, delta, fmt, onNavigate, href }: KpiCardProps) {
  const channels = useMemo(() => deltaChannels(delta, kind, fmt), [delta, kind, fmt]);
  const Icon = channels.icon;
  const aria = `${label}: ${fmt(current)} versus ${fmt(previous)}, ${channels.label}${safePercent(delta) ? ` (${safePercent(delta)})` : ''}`;
  const interactive = Boolean(href && onNavigate);
  const drillTestId = interactive ? `trends-v2-drill-kpi-${kind}` : undefined;
  return (
    <article
      role="article"
      aria-live="polite"
      aria-label={aria}
      data-testid={testId}
      className={cn(
        'flex h-full min-h-[120px] min-w-[44px] flex-col justify-between gap-1 rounded-lg border bg-card p-3 text-card-foreground shadow-sm',
      )}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold tabular-nums tracking-tight" data-role="kpi-current">
        {fmt(current)}
      </p>
      <div className="flex items-center justify-between gap-2 text-xs tabular-nums text-muted-foreground">
        <span>vs prior {fmt(previous)}</span>
        <span className={cn('flex items-center gap-0.5 font-medium', channels.textClass)}>
          <Icon className="h-3 w-3" aria-hidden="true" />
          <span aria-hidden="true">{channels.sign}</span>
          <span className="sr-only">{channels.label}</span>
          <span aria-hidden="true">{safePercent(delta)}</span>
        </span>
      </div>
      {interactive && onNavigate && drillTestId ? (
        <button
          type="button"
          onClick={onNavigate}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onNavigate(e);
            }
          }}
          data-testid={drillTestId}
          aria-label={`View ${label} transactions in this window`}
          className="mt-1 inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1 self-end rounded-md border border-border/60 px-2 text-xs text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-data-neutral"
        >
          <span>View</span>
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
        </button>
      ) : null}
    </article>
  );
}

// ── Main skin ───────────────────────────────────────────────────────────────

export function TrendsV2Skin({ state }: TrendsV2SkinProps) {
  const { data, filterState, granularity, onGranularityChange } = state;
  const { fmt } = useCurrency();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const drillHref = useDrillHref(filterState);
  const recurringColor = useDataToken('warning');
  const oneOffColor = useDataToken('neutral');
  const netColor = useDataToken('neutral');

  const handleNavigate = useCallback(
    (driver: DrillDriver, bucket?: string) => (e: React.MouseEvent | React.KeyboardEvent) => {
      const href = drillHref(driver, bucket);
      navigateFromEvent(navigate, href, e);
    },
    [drillHref, navigate],
  );

  // ── Window banner copy (Row 1) ────────────────────────────────────────
  const windowBannerCopy = useMemo(() => {
    const from = data.meta.window.from;
    const to = data.meta.window.to;
    const days = data.meta.window.days;
    const prevFrom = data.meta.previousWindow.from;
    const prevTo = data.meta.previousWindow.to;
    const prevDays = data.meta.previousWindow.days;
    return `From ${formatPrettyDate(from)} to ${formatPrettyDate(to)} (${days} days) vs ${formatPrettyDate(prevFrom)} to ${formatPrettyDate(prevTo)} (${prevDays} days)`;
  }, [data]);

  // ── KPI cards (Row 2) ─────────────────────────────────────────────────
  const kpis = useMemo(
    () => [
      { kind: 'spending' as const, label: 'Spending' },
      { kind: 'income' as const, label: 'Income' },
      { kind: 'net' as const, label: 'Net' },
    ],
    [],
  );

  // ── Top drivers (Row 3) ───────────────────────────────────────────────
  const topCategories = data.topCategories.slice(0, 5);
  const topMerchants = data.topMerchants.slice(0, 5);

  // ── Recurring vs one-off (Row 4) ──────────────────────────────────────
  const recurring = data.recurringVsOneOff.recurring;
  const oneOff = data.recurringVsOneOff.oneOff;
  const recurringShare = Math.max(0, Math.min(100, recurring.share ?? 0));
  const oneOffShare = Math.max(0, Math.min(100, oneOff.share ?? 0));

  // ── Anomalies (Row 5) ─────────────────────────────────────────────────
  const anomalies = useMemo(
    () =>
      [...data.anomalies]
        .sort((a, b) => (b.deviationScore ?? 0) - (a.deviationScore ?? 0))
        .slice(0, 3),
    [data.anomalies],
  );

  // ── Net-spend chart (Row 6) ───────────────────────────────────────────
  const chartConfig: ChartConfig = {
    net: { label: 'Net', color: netColor },
  };
  const chartData = useMemo(
    () =>
      data.series.map((b) => ({
        bucket: b.bucket,
        net: b.net,
      })),
    [data.series],
  );

  return (
    <div
      data-testid="trends-v2-root"
      className="flex w-full min-w-0 flex-col gap-4 overflow-hidden"
    >
      {/* 1. Window banner ───────────────────────────────────────────────── */}
      <section
        role="region"
        aria-label="Trends window summary"
        data-testid="trends-v2-window-banner"
        className="rounded-lg border bg-card px-3 py-2 text-sm text-muted-foreground"
      >
        <p className="break-words">{windowBannerCopy}</p>
      </section>

      {/* 2. Comparison KPI strip ───────────────────────────────────────── */}
      <section
        role="group"
        aria-label="Current versus previous period totals"
        data-testid="trends-v2-kpi-strip"
        className={cn(
          'grid gap-3',
          isMobile ? 'grid-cols-2' : 'grid-cols-3',
        )}
      >
        {kpis.map((k) => (
          <KpiCard
            key={k.kind}
            testId={`trends-v2-kpi-${k.kind}`}
            kind={k.kind}
            label={k.label}
            current={data.totals.current[k.kind]}
            previous={data.totals.previous[k.kind]}
            delta={data.totals.deltas[k.kind]}
            fmt={fmt}
          />
        ))}
      </section>

      {/* 3. Top drivers ─────────────────────────────────────────────────── */}
      <section
        aria-label="Top spending drivers"
        className="grid grid-cols-2 gap-3"
      >
        <div
          data-testid="trends-v2-top-categories"
          className="rounded-lg border bg-card p-3"
        >
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Top categories
          </h3>
          <ul role="list" className="flex flex-col gap-1">
            {topCategories.length === 0 && (
              <li className="text-xs text-muted-foreground">No category data in this window.</li>
            )}
            {topCategories.map((row) => {
              const slug = merchantSlug(row.name);
              // Uncategorized rows (categoryId === null) cannot be
              // drilled through a canonical categories filter — skip
              // the drill affordance and render as a read-only row.
              const canonicalId = row.categoryId;
              const isNavigable = typeof canonicalId === 'string' && canonicalId.length > 0;
              const rowId = isNavigable ? canonicalId as string : slug;
              const driver: DrillDriver = { kind: 'category', id: rowId, label: row.name };
              const handle = isNavigable ? handleNavigate(driver) : undefined;
              const ariaSuffix = isNavigable ? ', View transactions' : '';
              const ariaName = isNavigable
                ? `${row.name}, total ${fmt(row.total)}, ${deltaChannels(row.delta, 'spending', fmt).label}${ariaSuffix}`
                : `${row.name}, total ${fmt(row.total)}, ${deltaChannels(row.delta, 'spending', fmt).label}`;
              return (
                <li key={rowId}>
                  <DriverRow
                    testId={`trends-v2-drill-category-${rowId}`}
                    drillTestId={isNavigable ? `trends-v2-drill-category-${rowId}` : undefined}
                    label={row.name}
                    total={fmt(row.total)}
                    delta={row.delta}
                    ariaName={ariaName}
                    href={isNavigable ? drillHref(driver) : undefined}
                    drillKind={isNavigable ? 'category' : undefined}
                    drillId={isNavigable ? rowId : undefined}
                    onNavigate={handle}
                  />
                </li>
              );
            })}
          </ul>
        </div>
        <div
          data-testid="trends-v2-top-merchants"
          className="rounded-lg border bg-card p-3"
        >
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Top merchants
          </h3>
          <ul role="list" className="flex flex-col gap-1">
            {topMerchants.length === 0 && (
              <li className="text-xs text-muted-foreground">No merchant data in this window.</li>
            )}
            {topMerchants.map((row) => {
              const id = merchantSlug(row.merchant);
              const driver: DrillDriver = { kind: 'merchant', id, label: row.merchant };
              const handle = handleNavigate(driver);
              return (
                <li key={row.merchant}>
                  <DriverRow
                    testId={`trends-v2-drill-merchant-${id}`}
                    drillTestId={`trends-v2-drill-merchant-${id}`}
                    label={row.merchant}
                    total={fmt(row.total)}
                    delta={row.delta}
                    ariaName={`${row.merchant}, total ${fmt(row.total)}, ${deltaChannels(
                      row.delta,
                      'spending',
                      fmt,
                    ).label}, View transactions`}
                    href={drillHref(driver)}
                    drillKind="merchant"
                    drillId={id}
                    onNavigate={handle}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* 4. Recurring vs one-off ─────────────────────────────────────────── */}
      <section
        role="group"
        aria-label="Recurring versus one-off share"
        data-testid="trends-v2-recurring-vs-oneoff"
        className="rounded-lg border bg-card p-2"
      >
        <div className="mb-1 flex min-h-[44px] items-center justify-between gap-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Recurring vs one-off
          </h3>
          <IconButton
            tooltip="Recurring spending repeats across the selected window; one-off spending appears once."
            tooltipSide="top"
            variant="ghost"
            size="icon"
            className="h-11 w-11 min-h-[44px] min-w-[44px] shrink-0 p-0"
            data-testid="trends-v2-recurring-info"
          >
            <Info className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        </div>
        <div className="space-y-1">
          <div
            className="flex h-2 w-full overflow-hidden rounded-full bg-muted"
            role="img"
            aria-label={`Recurring ${recurringShare.toFixed(1)}%, one-off ${oneOffShare.toFixed(1)}%`}
          >
            <div
              style={{ width: `${recurringShare}%`, background: recurringColor }}
              data-testid="trends-v2-recurring-bar"
            />
            <div
              style={{ width: `${oneOffShare}%`, background: oneOffColor }}
              data-testid="trends-v2-oneoff-bar"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="flex items-center gap-1.5 tabular-nums">
              <span className="h-2 w-2 rounded-full" style={{ background: recurringColor }} aria-hidden="true" />
              <span className="text-muted-foreground">Recurring</span>
              <span className="font-medium text-foreground">{fmt(recurring.total)}</span>
              <span className="text-muted-foreground">({recurringShare.toFixed(1)}%)</span>
            </span>
            <span className="flex items-center gap-1.5 tabular-nums">
              <span className="h-2 w-2 rounded-full" style={{ background: oneOffColor }} aria-hidden="true" />
              <span className="text-muted-foreground">One-off</span>
              <span className="font-medium text-foreground">{fmt(oneOff.total)}</span>
              <span className="text-muted-foreground">({oneOffShare.toFixed(1)}%)</span>
            </span>
          </div>
        </div>
      </section>

      {/* 5. Anomaly callouts ─────────────────────────────────────────────── */}
      <section
        aria-label="Anomaly callouts"
        data-testid="trends-v2-anomalies"
        className="space-y-1"
      >
        <ul
          role="list"
          data-testid="trends-v2-anomaly-list"
          className="overflow-hidden rounded-md border bg-card divide-y divide-border"
        >
          {anomalies.length === 0 && (
            <li className="min-h-[44px] px-2 py-2 text-xs text-muted-foreground">
              No anomalies detected in this window.
            </li>
          )}
          {anomalies.map((a) => {
            const driver: DrillDriver | undefined =
              a.kind === 'category' && a.bucket
                ? { kind: 'category', id: a.bucket, label: a.bucket }
                : a.kind === 'merchant' && a.bucket
                  ? { kind: 'merchant', id: merchantSlug(a.bucket), label: a.bucket }
                  : undefined;
            const ariaLabel = `${a.bucket}, value ${fmt(a.value)}, expected ${fmt(a.expectedRange.low)} to ${fmt(a.expectedRange.high)}`;
            return (
              <li key={`${a.kind}-${a.bucket}`}>
                <article
                  role="article"
                  aria-label={ariaLabel}
                  data-testid={`trends-v2-drill-anomaly-${a.kind}-${a.bucket}`}
                  className="flex min-h-[44px] items-center gap-2 px-2 py-1.5"
                >
                  <div className="min-w-0 flex-1">
                    <p data-role="anomaly-date" className="break-words text-sm font-medium">
                      {a.kind === 'day' ? formatPrettyDate(a.bucket) : a.bucket}
                    </p>
                    <p data-role="anomaly-value" className="text-sm font-semibold tabular-nums" style={{ color: netColor }}>
                      {fmt(a.value)}
                    </p>
                    <p data-role="anomaly-details" className="break-words text-xs tabular-nums text-muted-foreground">
                      expected {fmt(a.expectedRange.low)} – {fmt(a.expectedRange.high)} · deviation {a.deviationScore.toFixed(1)}σ
                    </p>
                  </div>
                  {driver ? (
                    <button
                      type="button"
                      onClick={handleNavigate(driver, a.bucket)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleNavigate(driver, a.bucket)(e);
                        }
                      }}
                      data-testid={`trends-v2-drill-anomaly-${a.kind}-${a.bucket}-open`}
                      aria-label="View anomaly transactions in this window"
                      className="mt-0 inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center gap-1 rounded-md border border-border/60 px-2 text-xs hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-data-neutral"
                    >
                      View <ChevronRight className="h-3 w-3" aria-hidden="true" />
                    </button>
                  ) : null}
                </article>
              </li>
            );
          })}
        </ul>
      </section>

      {/* 6. Net-spend chart ─────────────────────────────────────────────── */}
      <section
        aria-label="Net spend area chart"
        className="rounded-lg border bg-card p-3"
      >
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Net spend ({granularLongForm(granularity)})
          </h3>
        </div>
        <div
          role="img"
          aria-label={`Net spend area chart, ${granularity} buckets from ${data.meta.window.from} to ${data.meta.window.to}`}
          data-testid="trends-v2-chart"
          className={cn(INSIGHT_CHART_CONTAINER_CLASS, 'min-h-[280px]')}
        >
          <ChartContainer config={chartConfig} className={INSIGHT_CHART_CONTAINER_CLASS}>
            <AreaChart data={chartData} margin={INSIGHT_CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" vertical={false} />
              <XAxis
                dataKey="bucket"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tick={{ fontSize: 10 }}
                angle={isMobile ? -45 : 0}
                textAnchor={isMobile ? 'end' : 'middle'}
                height={isMobile ? 60 : 30}
                tickFormatter={(v: string) => bucketLabel(v, granularity)}
                interval="preserveStartEnd"
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={36}
                tick={{ fontSize: 10 }}
                tickFormatter={(v) =>
                  Math.abs(Number(v)) >= 1000
                    ? `${(Number(v) / 1000).toFixed(0)}k`
                    : String(v)
                }
              />
<ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(label) => bucketLabel(String(label), granularity)}
                    formatter={(value) => fmt(Number(value))}
                  />
                }
              />
              <Area
                type="monotone"
                dataKey="net"
                stroke={netColor}
                fill={netColor}
                fillOpacity={0.18}
                strokeWidth={2}
                isAnimationActive={false}
              />
            </AreaChart>
          </ChartContainer>
        </div>
      </section>

      {/* 7. Granularity segmented control ──────────────────────────────── */}
      <section
        role="radiogroup"
        aria-label="Chart granularity"
        data-testid="trends-v2-granularity"
        className={cn(
          'grid grid-cols-3 gap-2',
          isMobile ? 'w-full' : 'ml-auto w-full sm:w-auto sm:min-w-[360px]',
        )}
      >
        {(['day', 'week', 'month'] as const).map((g) => {
          const checked = granularity === g;
          return (
            <button
              key={g}
              type="button"
              role="radio"
              aria-checked={checked}
              aria-label={g.charAt(0).toUpperCase() + g.slice(1)}
              data-state={checked ? 'on' : 'off'}
              data-testid={`trends-v2-granularity-${g}`}
              style={checked ? { backgroundColor: netColor, borderColor: netColor } : undefined}
              onClick={() => {
                if (!checked) onGranularityChange(g);
              }}
              className={cn(
                'inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md border px-3 text-sm font-medium transition-colors duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-data-neutral',
                checked
                  ? 'text-white shadow-sm'
                  : 'border-border/60 bg-background text-foreground hover:bg-muted',
              )}
            >
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          );
        })}
      </section>
    </div>
  );
}

export default TrendsV2Skin;
